import {
  hasLatestRunPostingSuccess,
  resolvePostingProcessingFlag,
} from './chatPageViewUtils';

export const createEmptySessionState = () => ({
  jd: {
    text: '',
    loading: false,
    error: null,
    analysisPhase: 'initial',
    waitingForConfirmation: false,
    requirements: {},
    missingFields: [],
  },
  chat: {
    systemMessages: [],
    systemMessage: '',
    chatHistory: [],
    apiLoading: false,
    lifecycleLoading: false,
  },
  lifecycle: {
    awaitingConfirmation: false,
    scheduleActive: false,
    actions: [],
    lastInteraction: 0,
  },
  jobPosting: {
    linkedinLogs: [],
    naukriLogs: [],
    linkedinProcessing: false,
    naukriProcessing: false,
    phase: 'idle',
    selectedChannels: [],
    postedChannels: [],
    processing: false,
    complete: false,
    failed: false,
    waitingForQuestion: false,
    pendingField: null,
    pendingFieldOptions: [],
    collectedDetails: null,
  },
  sourcing: {
    googleLogs: [],
    linkedinLogs: [],
    resumeBoxLogs: [],
    googleProcessing: false,
    linkedinProcessing: false,
    resumeBoxProcessing: false,
    googleCandidateCount: 0,
    linkedinCandidateCount: 0,
    resumeBoxCandidateCount: 0,
    started: false,
    complete: false,
    candidates: [],
    stats: {},
  },
  outreach: {
    logs: [],
    phase: null,
    sending: false,
    completed: false,
  },
  agentStage: {
    label: '',
    cancellable: false,
  },
  filtering: {
    chatMode: 'agent',
    breadcrumbs: [],
    actions: [],
    workingCandidates: [],
  },
  searchId: null,
  savedAt: null,
});

export const getPersistedJobPostingState = (sessionId) => {
  if (!sessionId) return null;

  try {
    const savedJobPosting = localStorage.getItem(`job_posting_state_${sessionId}`);
    if (!savedJobPosting) return null;

    const jp = JSON.parse(savedJobPosting);
    if (jp?.savedAt && Date.now() - jp.savedAt < 30 * 60 * 1000) {
      return jp;
    }

    localStorage.removeItem(`job_posting_state_${sessionId}`);
  } catch (e) {
    console.warn('[localStorage] Failed to restore job posting state:', e);
  }

  return null;
};

export const normalizeJobPostingState = (jobPostingState = {}) => {
  const state = jobPostingState || {};
  const linkedinProcessing = resolvePostingProcessingFlag(state.linkedinLogs || [], Boolean(state.linkedinProcessing));
  const naukriProcessing = resolvePostingProcessingFlag(state.naukriLogs || [], Boolean(state.naukriProcessing));
  const activePlatformProcessing = linkedinProcessing || naukriProcessing;
  const derivedPostedChannels = [
    ...(hasLatestRunPostingSuccess(state.linkedinLogs || []) ? ['LinkedIn'] : []),
    ...(hasLatestRunPostingSuccess(state.naukriLogs || []) ? ['Naukri'] : []),
  ];
  const postedChannels = Array.from(new Set([
    ...(Array.isArray(state.postedChannels) ? state.postedChannels : []),
    ...derivedPostedChannels,
  ]));
  const hasTerminalLogs = (state.linkedinLogs?.length || 0) > 0 || (state.naukriLogs?.length || 0) > 0;
  const fieldCollectionActive =
    Boolean(state.fieldCollectionActive) ||
    Boolean(state.pendingField) ||
    Boolean(state.pendingFieldsQueue?.length);
  const failed = Boolean(state.failed);
  const complete = Boolean(state.complete) || postedChannels.length > 0;
  const processing = activePlatformProcessing;

  let phase = state.phase || 'idle';
  if (fieldCollectionActive) {
    phase = phase === 'channel_selection' ? 'channel_selection' : 'collecting_details';
  } else if (processing) {
    phase = 'posting';
  } else if (complete || failed || hasTerminalLogs) {
    phase = 'posted';
  } else {
    phase = 'idle';
  }

  return {
    ...state,
    phase,
    postedChannels,
    processing,
    complete: complete || (!processing && (failed || postedChannels.length > 0)),
    failed,
    linkedinProcessing,
    naukriProcessing,
    fieldCollectionActive,
  };
};

export const mergeJobPostingState = (baseJobPosting = {}, persistedJobPosting = null) => {
  if (!persistedJobPosting) return baseJobPosting || {};

  const base = baseJobPosting || {};
  const persisted = persistedJobPosting || {};
  const mergedLinkedinLogs =
    (base.linkedinLogs || []).length >= (persisted.linkedinLogs || []).length
      ? (base.linkedinLogs || [])
      : (persisted.linkedinLogs || []);
  const mergedNaukriLogs =
    (base.naukriLogs || []).length >= (persisted.naukriLogs || []).length
      ? (base.naukriLogs || [])
      : (persisted.naukriLogs || []);
  const mergedLinkedinProcessing = Boolean(base.linkedinProcessing || persisted.linkedinProcessing);
  const mergedNaukriProcessing = Boolean(base.naukriProcessing || persisted.naukriProcessing);
  const mergedSelectedChannels = persisted.processing || persisted.linkedinProcessing || persisted.naukriProcessing
    ? (persisted.selectedChannels || [])
    : base.processing || base.linkedinProcessing || base.naukriProcessing
    ? (base.selectedChannels || [])
    : (base.selectedChannels?.length ? base.selectedChannels : (persisted.selectedChannels || []));
  const mergedPostedChannels = Array.from(
    new Set([...(base.postedChannels || []), ...(persisted.postedChannels || [])]),
  );
  const mergedProcessing =
    Boolean(base.processing || persisted.processing || mergedLinkedinProcessing || mergedNaukriProcessing);

  return normalizeJobPostingState({
    ...persisted,
    ...base,
    phase: mergedProcessing ? 'posting' : (base.phase || persisted.phase || 'idle'),
    selectedChannels: mergedSelectedChannels,
    postedChannels: mergedPostedChannels,
    processing: mergedProcessing,
    linkedinProcessing: mergedLinkedinProcessing,
    naukriProcessing: mergedNaukriProcessing,
    linkedinLogs: mergedLinkedinLogs,
    naukriLogs: mergedNaukriLogs,
  });
};
